import { i, s, x } from '../../lit-element.js';

const style = i`
  .doc-viewer {
    background-color: #d4d4d4;
    border: 1px solid #d4d4d4;
    box-sizing: border-box;
    margin: 0 auto;
    overflow: scroll;
    width: 100%;
  }

  .doc-nav-bar {
    align-content: center;
    align-items: center;
    background-color: #141414;
    display: flex;
    justify-content: space-between;
    min-height: 50px;
  }

  .doc-nav-bar p {
    color: #fff;
    word-spacing: 0.5em;
  }

  button,
  a {
    background-color: #141414;
    border: 1px solid #141414;
    color: $ods-color__white;
    margin: 0.5em;
  }

  button:focus,
  a:focus {
    outline: 1px dashed #777;
  }

  button > svg,
  a > svg {
    height: 1.5em;
    width: 1.5em;
  }

  @media (min-width: 48em) {
    button > svg,
    a > svg {
      height: 2.5em;
      width: 2.5em;
    }
  }
`;

/* global pdfjsLib */

class RefiDocViewer extends s {
  constructor() {
    super();
    this.canvas = null;
    this.docViewport = null;
    this.doc = null;
    this.page = null;
    this.numOfPages = null;
    this.currentPage = null;
    this.scale = null;
  }

  static get properties() {
    return {
      currentPage: { type: String },
      src: { type: String, attribute: true },
    };
  }

  static get styles() {
    return style;
  }

  render() {
    return x`
      <div class="doc-nav-bar">
        <div>
          <button
            type="button"
            @click="${() => {
              this.renderPage((this.currentPage -= 1));
            }}"
            aria-label="previous page"
            data-e2e-ref="pdfPrevBtn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFFFFF">
              <path d="M14.5 19a.5.5 0 01-.354-.146l-7-7a.5.5 0 010-.708l7-7a.5.5 0 01.708.708L8.207 11.5l6.647 6.646A.5.5 0 0114.5 19z" />
            </svg>
          </button>
          <button
            type="button"
            @click="${() => {
              this.renderPage((this.currentPage += 1));
            }}"
            aria-label="next page"
            data-e2e-ref="pdfNextBtn"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFFFFF">
              <path d="M9.5 19a.5.5 0 01-.354-.854l6.647-6.646-6.647-6.646a.5.5 0 01.708-.708l7 7a.5.5 0 010 .708l-7 7A.5.5 0 019.5 19z" />
            </svg>
          </button>
        </div>
        <p data-e2e-ref="pdfPagination">${this.currentPage} of ${this.numOfPages}</p>
        <div>
          <button
            type="button"
            @click="${() => {
              this.renderZoom((this.scale -= 0.25));
            }}"
            aria-label="zoom out"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFFFFF">
              <path d="M18.5 12h-13a.5.5 0 010-1h13a.5.5 0 010 1z" />
            </svg>
          </button>
          <button
            type="button"
            @click="${() => {
              this.renderZoom((this.scale += 0.25));
            }}"
            aria-label="zoom in"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFFFFF">
              <path d="M19 11.5a.5.5 0 01-.5.5h-6v6a.5.5 0 01-1 0v-6h-6a.5.5 0 010-1h6V5a.5.5 0 011 0v6h6a.5.5 0 01.5.5z" />
            </svg>
          </button>
        </div>
        <a href="${this.src}" @click="${this.blobDownload}" aria-label="download" download="autorefi_contract.pdf">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#FFFFFF">
            <path
              d="M21 14.5v5a1.5 1.5 0 01-1.5 1.5h-15A1.5 1.5 0 013 19.5v-5a.5.5 0 011 0v5a.5.5 0 00.5.5h15a.5.5 0 00.5-.5v-5a.5.5 0 011 0zm-9.357.354A.5.5 0 0012 15a.494.494 0 00.362-.159l4.992-4.987a.5.5 0 00-.708-.708L12.5 13.289V3.5a.5.5 0 00-1 0v9.8L7.354 9.146a.5.5 0 00-.708.708z"
            />
          </svg>
        </a>
      </div>
      <div class="doc-viewer" tabindex="0" @keydown="${this.keyboardControls}">
        <canvas></canvas>
      </div>
    `;
  }

  async firstUpdated() {
    this.canvas = this.shadowRoot.querySelector('canvas');
    this.docViewport = this.shadowRoot.querySelector('.doc-viewer');
    await this.loadPDFJS();
  }

  keyboardControls(ev) {
    let handled = false;
    const cmd = (ev.ctrlKey ? 1 : 0) + (ev.altKey ? 2 : 0) + (ev.shiftKey ? 4 : 0) + (ev.metaKey ? 8 : 0);
    // no control key pressed at all
    if (cmd === 0) {
      switch (ev.keyCode) {
        case 37: // left arrow
        case 75: // 'k'
        case 80: // 'p'
          if (this.docViewport.scrollWidth <= this.docViewport.clientWidth) {
            this.renderPage((this.currentPage -= 1));
            handled = true;
          }
          break;
        case 39: // right arrow
        case 74: // 'j'
        case 78: // 'n'
          if (this.docViewport.scrollWidth <= this.docViewport.clientWidth) {
            this.renderPage((this.currentPage += 1));
            handled = true;
          }
          break;
        case 36: // home
          this.renderPage();
          handled = true;
          break;
        case 35: // end
          this.renderPage((this.currentPage = this.numOfPages));
          handled = true;
          break;
        // no default case
      }
    }
    // either CTRL or META key with optional SHIFT. handles zooming
    if (cmd === 1 || cmd === 8 || cmd === 5 || cmd === 12) {
      switch (ev.keyCode) {
        case 61: // FF/Mac '='
        case 107: // FF '+' and '='
        case 187: // Chrome '+'
          this.renderZoom((this.scale += 0.25));
          handled = true;
          break;
        case 173: // FF/Mac '-'
        case 109: // FF '-'
        case 189: // Chrome '-'
          this.renderZoom((this.scale -= 0.25));
          handled = true;
          break;
        case 48: // '0'
          this.renderPage(this.currentPage);
          handled = true;
          break;
        // no default case
      }
    }

    // CTRL or META without shift with 's'
    if ((cmd === 1 || cmd === 8) && ev.keyCode === 83) {
      this.shadowRoot.querySelector('a').click();
      handled = true;
    }

    if (handled) {
      ev.preventDefault();
    }
  }

  loadPDFJS() {
    const scriptTag = document.createElement('script');
    scriptTag.setAttribute('type', 'module');
    scriptTag.setAttribute('src', 'pdf.min.mjs');
    document.querySelector('head').appendChild(scriptTag);
    scriptTag.addEventListener('load', this.loadPDF.bind(this));
  }

  async loadPDF() {
    pdfjsLib.GlobalWorkerOptions.workerSrc = './pdf.worker.min.mjs';
    this.doc = await pdfjsLib.getDocument(this.src).promise;
    this.numOfPages = this.doc.numPages;
    await this.renderPage(1);
  }

  async renderPage(num = 1) {
    this.currentPage = Math.max(Math.min(num, this.numOfPages), 1);
    this.page = await this.doc.getPage(this.currentPage);
    const viewport = this.page.getViewport({ scale: 1 });
    this.scale = this.docViewport.clientWidth / viewport.width;
    this.docViewport.style.height = `${viewport.height * this.scale + 10}px`;
    this.renderPDF();
  }

  renderZoom(scale) {
    this.scale = Math.max(Math.min(scale, 2), 0.25);
    this.renderPDF();
  }

  renderPDF() {
    const devicePixelRatio = window.devicePixelRatio || 1;
    const viewport = this.page.getViewport({ scale: this.scale });
    const ctx = this.canvas.getContext('2d');
    this.canvas.style.height = `${viewport.height}px`;
    this.canvas.style.width = `${viewport.width}px`;
    this.canvas.height = viewport.height * devicePixelRatio;
    this.canvas.width = viewport.width * devicePixelRatio;
    const transform = [devicePixelRatio, 0, 0, devicePixelRatio, 0, 0];
    const renderContext = {
      canvasContext: ctx,
      viewport,
      transform,
    };
    this.page.render(renderContext);
  }

  blobDownload() {
    if ('msSaveBlob' in navigator) {
      this.dispatchEvent(new CustomEvent('ms-download-blob'));
      return false;
    }
    return true;
  }
}

export { RefiDocViewer };
